import React from "react";
import PropTypes from "prop-types";
import {
  Dialog,
  Box,
  Typography,
  Button,
  IconButton,
  Divider,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import NotificationsNoneOutlinedIcon from "@mui/icons-material/NotificationsNoneOutlined";
import OutlinedFlagIcon from "@mui/icons-material/OutlinedFlag";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import PlayArrowRoundedIcon from "@mui/icons-material/PlayArrowRounded";
import { useNavigate } from "react-router-dom";
import apiClient from "../Utils/apiClient";
import socketService from "../socket/newsocketService";

const getInitials = (name) => {
  if (!name) return "AB";
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return "AB";
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
};

const formatTimeLabel = (timestamp) => {
  const date = timestamp ? new Date(timestamp) : new Date();
  if (isNaN(date.getTime())) {
    return "Just now";
  }
  const timeStr = date.toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
  return `Just now, ${timeStr}`;
};

const DriverEscortModal = ({ open, onClose, data }) => {
  const navigate = useNavigate();

  if (!open) return null;

  const payload = data || {};
  const driverObj = payload?.driver || payload?.trip?.driverId || payload?.trip?.driver || {};
  const tripObj = payload?.trip || {};
  const userObj = payload?.user || {};

  // Driver Name
  const driverName =
    (driverObj?.driverName && `${driverObj?.driverName} ${driverObj?.surName || ""}`.trim()) ||
    driverObj?.name ||
    payload?.driverName ||
    userObj?.driverName ||
    (userObj?.userName && userObj?.isDriver ? userObj?.userName : null) ||
    payload?.userName ||
    "Abdullahi Bello";

  const initials = getInitials(driverName);

  // Escort ID
  let escortId =
    payload?.escortId ||
    payload?.tripId ||
    tripObj?.tripId ||
    tripObj?._id ||
    payload?._id ||
    payload?.id;

  if (!escortId) {
    escortId = "ESC-20482";
  } else if (!String(escortId).toUpperCase().startsWith("ESC-")) {
    const rawStr = String(escortId);
    escortId = `ESC-${rawStr.length > 5 ? rawStr.slice(-5).toUpperCase() : rawStr.toUpperCase()}`;
  }

  // Plate Number
  const plateNumber =
    driverObj?.regNo ||
    driverObj?.vehiclePlate ||
    driverObj?.vehicleReg ||
    driverObj?.licensePlate ||
    tripObj?.vehiclePlate ||
    tripObj?.licensePlate ||
    payload?.regNo ||
    payload?.plateNumber ||
    payload?.vehiclePlate ||
    payload?.licensePlate ||
    payload?.vehicleReg ||
    payload?.VhiclePlate ||
    "KDN 482 AB";

  // Current Location
  const rawCurrentLocation =
    payload?.currentLocation ||
    tripObj?.currentLocation ||
    payload?.pickupLocation ||
    tripObj?.pickupLocation ||
    payload?.originCity ||
    tripObj?.originCity ||
    payload?.state ||
    userObj?.state ||
    driverObj?.state;

  const currentLocation =
    rawCurrentLocation && String(rawCurrentLocation).trim() !== ""
      ? String(rawCurrentLocation).trim()
      : "Barnawa, Kaduna";

  // Destination
  const rawDestination =
    payload?.destination ||
    tripObj?.destination ||
    tripObj?.finalDestination ||
    payload?.finalDestination ||
    payload?.deliveryLocation ||
    tripObj?.deliveryLocation ||
    payload?.destinationCity ||
    tripObj?.destinationCity;

  const hasDestination = Boolean(
    rawDestination &&
      String(rawDestination).trim() !== "" &&
      !["no destination entered", "no destination", "none", "null", "undefined", "no"].includes(
        String(rawDestination).trim().toLowerCase()
      )
  );

  const destination = hasDestination ? String(rawDestination).trim() : "No destination entered";

  // Time
  const timeDisplay = formatTimeLabel(
    payload?.timestamp ||
      payload?.createdAt ||
      tripObj?.createdAt ||
      payload?.currentTimeStamp
  );

  const handleStartEscort = async () => {
    const tripId = payload?.tripId || tripObj?._id || payload?._id;
    const userId = payload?.userObjectKey || userObj?._id || payload?.userId || driverObj?._id;

    // Optional API notification to update status
    if (tripId) {
      try {
        await apiClient.put(`/escort-trip/start/${tripId}`);
      } catch (err) {
        try {
          await apiClient.put(`/escort-trip/update-status/${tripId}`, {
            status: "STARTED",
          });
        } catch (_) {}
      }
    }

    // Socket notification
    try {
      socketService.emit("driverEscortStarted", {
        tripId,
        driverId: driverObj?._id || payload?.driverId,
        userId,
        ...payload,
      });
    } catch (_) {}

    // Refresh escort table listener
    try {
      window.dispatchEvent(new CustomEvent("refreshEscortTable"));
    } catch (_) {}

    onClose();

    // Navigate to escort page
    navigate("/escort", {
      state: {
        escort: {
          ...payload,
          userName: driverName,
          driverName,
          plateNumber,
          currentLocation,
          destination,
        },
        tripData: tripObj || payload,
        pickupLocation: currentLocation,
        deliveryLocation: destination,
        isTripActive: true,
      },
    });
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      PaperProps={{
        sx: {
          borderRadius: "20px",
          maxWidth: "480px",
          width: "100%",
          p: { xs: 2.5, sm: 3 },
          boxShadow: "0 20px 45px rgba(0, 0, 0, 0.16)",
          overflow: "hidden",
          backgroundColor: "#FFFFFF",
        },
      }}
    >
      {/* Header */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: "flex-start",
          mb: 2.5,
        }}
      >
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.75 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              bgcolor: "#FEE2E2",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexShrink: 0,
            }}
          >
            <NotificationsNoneOutlinedIcon sx={{ color: "#EF4444", fontSize: 24 }} />
          </Box>
          <Box>
            <Typography
              sx={{
                fontSize: "17px",
                fontWeight: 700,
                color: "#111827",
                lineHeight: 1.25,
              }}
            >
              New escort request
            </Typography>
            <Typography
              sx={{
                fontSize: "13px",
                color: "#6B7280",
                mt: 0.4,
              }}
            >
              {timeDisplay}
            </Typography>
          </Box>
        </Box>
        <IconButton
          onClick={onClose}
          size="small"
          sx={{
            color: "#111827",
            p: 0.5,
            "&:hover": { bgcolor: "#F3F4F6" },
          }}
        >
          <CloseIcon sx={{ fontSize: 20 }} />
        </IconButton>
      </Box>

      {/* Driver Card */}
      <Box
        sx={{
          bgcolor: "#F8FAFC",
          borderRadius: "14px",
          p: 1.75,
          display: "flex",
          alignItems: "center",
          gap: 1.75,
          mb: 2.5,
        }}
      >
        <Box
          sx={{
            width: 44,
            height: 44,
            borderRadius: "50%",
            bgcolor: "#DBEAFE",
            color: "#1D4ED8",
            fontWeight: 700,
            fontSize: "16px",
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            flexShrink: 0,
          }}
        >
          {initials}
        </Box>
        <Box>
          <Typography
            sx={{
              fontSize: "15px",
              fontWeight: 700,
              color: "#0F172A",
              lineHeight: 1.25,
            }}
          >
            {driverName}
          </Typography>
          <Typography
            sx={{
              fontSize: "13px",
              color: "#64748B",
              fontWeight: 400,
              mt: 0.3,
            }}
          >
            Driver
          </Typography>
        </Box>
      </Box>

      {/* Escort ID & Plate Number */}
      <Box sx={{ mb: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 0.75,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
            Escort ID
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#0F172A",
            }}
          >
            {escortId}
          </Typography>
        </Box>

        <Divider sx={{ borderColor: "#F1F5F9", my: 0.75 }} />

        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "center",
            py: 0.75,
          }}
        >
          <Typography sx={{ fontSize: "14px", color: "#64748B" }}>
            Plate number
          </Typography>
          <Typography
            sx={{
              fontSize: "14px",
              fontWeight: 600,
              color: "#0F172A",
            }}
          >
            {plateNumber}
          </Typography>
        </Box>
      </Box>

      {/* Location Journey (Current -> Destination) */}
      <Box sx={{ mb: 2.5 }}>
        {/* Current Location */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
          <Box
            sx={{
              width: 20,
              display: "flex",
              justifyContent: "center",
              pt: "2px",
              flexShrink: 0,
            }}
          >
            {/* Red Pin SVG */}
            <svg
              width="18"
              height="20"
              viewBox="0 0 24 24"
              fill="none"
              stroke="#B91C1C"
              strokeWidth="2.2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <path d="M12 2a8 8 0 0 0-8 8c0 5.25 8 12 8 12s8-6.75 8-12a8 8 0 0 0-8-8z" />
              <circle cx="12" cy="10" r="3" />
            </svg>
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", color: "#64748B", lineHeight: 1.2 }}>
              Current location
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#0F172A",
                mt: 0.3,
              }}
            >
              {currentLocation}
            </Typography>
          </Box>
        </Box>

        {/* Connecting Line */}
        <Box
          sx={{
            width: "2px",
            height: "22px",
            bgcolor: "#E2E8F0",
            ml: "9px",
            my: 0.4,
          }}
        />

        {/* Destination */}
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1.75 }}>
          <Box
            sx={{
              width: 20,
              display: "flex",
              justifyContent: "center",
              pt: "2px",
              flexShrink: 0,
            }}
          >
            <OutlinedFlagIcon sx={{ color: "#334155", fontSize: 20 }} />
          </Box>
          <Box>
            <Typography sx={{ fontSize: "12px", color: "#64748B", lineHeight: 1.2 }}>
              Destination
            </Typography>
            <Typography
              sx={{
                fontSize: "14px",
                fontWeight: 600,
                color: "#0F172A",
                mt: 0.3,
              }}
            >
              {destination}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Warning banner shown ONLY when no destination is entered */}
      {!hasDestination && (
        <Box
          sx={{
            bgcolor: "#FDE68A",
            borderRadius: "10px",
            px: 1.75,
            py: 1.4,
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            mb: 2.5,
          }}
        >
          <InfoOutlinedIcon sx={{ color: "#78350F", fontSize: 20, flexShrink: 0 }} />
          <Typography
            sx={{
              fontSize: "13px",
              fontWeight: 500,
              color: "#78350F",
              lineHeight: 1.35,
            }}
          >
            Track until a wellbeing check confirms the trip has ended.
          </Typography>
        </Box>
      )}

      {/* Action Button: Start Escort */}
      <Button
        fullWidth
        variant="contained"
        onClick={handleStartEscort}
        startIcon={<PlayArrowRoundedIcon sx={{ fontSize: 22 }} />}
        sx={{
          bgcolor: "#2563EB",
          color: "#FFFFFF",
          py: 1.35,
          borderRadius: "10px",
          fontSize: "15px",
          fontWeight: 600,
          textTransform: "none",
          boxShadow: "none",
          "&:hover": {
            bgcolor: "#1D4ED8",
            boxShadow: "none",
          },
        }}
      >
        Start escort
      </Button>
    </Dialog>
  );
};

DriverEscortModal.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  data: PropTypes.object,
};

export default DriverEscortModal;
